import { deploymentRegionSchema } from "../utils/deployment";
import { defineEnvSchema, type EnvSchemaContext } from "./schema-definition";
import { z } from "zod";

const requiredIf = (required: boolean) =>
  required ? z.string() : z.string().optional();

const getClientEnvBaseSchema = ({ nodeEnv }: EnvSchemaContext) => ({
  NEXT_DEPLOYMENT_ID: z.string().optional(),
  NEXT_PUBLIC_SENTRY_DSN: z.string(),
  NEXT_PUBLIC_STYTCH_TOKEN: z.string(),
  // Both capture keys hold the token of this region's PostHog project; /ph sends to that region's host.
  NEXT_PUBLIC_POSTHOG_MARKETING_CAPTURE_KEY: requiredIf(
    nodeEnv === "production"
  ),
  NEXT_PUBLIC_POSTHOG_CAPTURE_KEY: z.string().optional(),
  NEXT_PUBLIC_DEPLOYMENT_REGION: deploymentRegionSchema.default("missing"),
  NEXT_PUBLIC_SITE_URL: z.string().default("https://listenlabs.ai"),
  // Extra app origins that may share this deploy's auth cookies (US cutover:
  // SITE_URL=https://listenlabs.ai and this = https://app.listenlabs.com).
  // Comma-separated. Unset on EU. Preview hosts stay on VERCEL_* not this.
  NEXT_PUBLIC_SITE_URL_ALIASES: z.string().optional(),
  // Marketing website (apex). App shells use SITE_URL (future app./eu.).
  NEXT_PUBLIC_MARKETING_SITE_URL: z.string().default("https://listenlabs.com"),
  NEXT_PUBLIC_DOCS_SITE_URL: z.string().default("https://docs.listenlabs.com"),
  NEXT_PUBLIC_BLOG_SITE_URL: z.string().default("https://blog.listenlabs.ai"),
  NEXT_PUBLIC_TRUST_SITE_URL: z.string().default("https://trust.listenlabs.ai"),
  NEXT_PUBLIC_ADMIN_URL: z.string().default("https://admin.listenlabs.ai"),
  NEXT_PUBLIC_LOCAL_SUPABASE_PROXY: z
    .enum(["true", "false"])
    .optional()
    .transform((value) => nodeEnv === "development" && value === "true"),
  NEXT_PUBLIC_SUPABASE_URL: z.string(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string(),
  NEXT_PUBLIC_SUPABASE_READONLY_URL: z.string().optional(),
  NEXT_PUBLIC_MUX_TRACKING_KEY: z.string().optional(),
  // Datadog RUM application (infra/datadog); RUM stays off while unset.
  NEXT_PUBLIC_DATADOG_RUM_APPLICATION_ID: z.string().optional(),
  NEXT_PUBLIC_AG_GRID_LICENSE_KEY: z.string().optional(),
  NEXT_PUBLIC_VERCEL_ENV: z.string().optional(),
  NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
  // Browser mirror of OTEL_RESOURCE_ATTRIBUTES; the staging project runs with
  // VERCEL_ENV=production, so the browser cannot infer its environment.
  NEXT_PUBLIC_OTEL_RESOURCE_ATTRIBUTES: z.string().optional(),
  // Stable per-branch preview alias. Auto-exposed by Vercel alongside
  // NEXT_PUBLIC_VERCEL_URL; auth callbacks must stay on whichever of the two
  // the user opened so the pkce verifier cookie is readable.
  NEXT_PUBLIC_VERCEL_BRANCH_URL: z.string().optional(),
});

export const nextAppEnvDefinition = defineEnvSchema({
  envConsumers: ["next-app"],
  schema: (context) => {
    const isVercelDeployment =
      context.vercelEnv === "production" || context.vercelEnv === "preview";

    return z.object({
      ...getClientEnvBaseSchema(context),
      APOLLO_API_KEY: z.string(),
      // Read only by the ap@ inbound route, so it stays out of the workers
      POSTMARK_AP_WEBHOOK_USERNAME: z.string().optional(),
      POSTMARK_AP_WEBHOOK_PASSWORD: z.string().optional(),
      // Read only by the Mux webhook route, so it stays out of the workers
      MUX_DEIDENTIFIED_WEBHOOK_SECRET: z.string().optional(),
      NEXT_PUBLIC_POSTHOG_CAPTURE_KEY: requiredIf(isVercelDeployment),
      POSTHOG_API_KEY: requiredIf(isVercelDeployment),
      POSTHOG_ENV_ID: requiredIf(isVercelDeployment),

      RATE_LIMIT_REDIS_KV_REST_API_URL: z.string(),
      RATE_LIMIT_REDIS_KV_REST_API_TOKEN: z.string(),
      RATE_LIMIT_REDIS_US_EAST_IAD1_KV_REST_API_URL: z.string(),
      RATE_LIMIT_REDIS_US_EAST_IAD1_KV_REST_API_TOKEN: z.string(),
      RATE_LIMIT_REDIS_EU_WEST_DUB1_KV_REST_API_URL: z.string(),
      RATE_LIMIT_REDIS_EU_WEST_DUB1_KV_REST_API_TOKEN: z.string(),
      RATE_LIMIT_REDIS_JAPAN_N_EAST_HND1_KV_REST_API_URL: z.string(),
      RATE_LIMIT_REDIS_JAPAN_N_EAST_HND1_KV_REST_API_TOKEN: z.string(),

      CEREBRAS_API_KEY: z.string(),

      DEPLOYMENT_REGION: deploymentRegionSchema.default("missing"),
      // Service identity (serviceIdentity.ts); see sharedBackendEnv.
      OTEL_SERVICE_NAME: z.string().optional(),
      OTEL_RESOURCE_ATTRIBUTES: z.string().optional(),

      VIDEO_PREVIEW_BASE_URL: z.string(),
      VIDEO_RENDER_BASE_URL: z.string(),
      REELS_GCS_BUCKET: z.string(),
      PARTIAL_RENDER_PUBSUB_TOPIC: z.string().default("partial-render-hls"),
      GOOGLE_CLOUD_PROJECT: z.string().default("merlin-agi"),

      TURNSTILE_SECRET_KEY: requiredIf(isVercelDeployment),
      PASSWORD_LOGIN_CAPTCHA_BYPASS_IPS: z
        .string()
        .default("")
        .transform((value) =>
          value
            .split(",")
            .map((ip) => ip.trim())
            .filter(Boolean)
        )
        .pipe(z.array(z.string().ip())),
      STYTCH_PROJECT_ID: z.string(),
      STYTCH_SECRET: z.string(),
      DEFAULT_DOT_COM_API_SECRET: z.string().optional(),

      EDITFRAME_API_KEY: requiredIf(isVercelDeployment),
      EDITFRAME_API_HOST: z.string().optional(),
      EDITFRAME_WEBHOOK_SECRET: requiredIf(isVercelDeployment),
      MCP_INTERNAL_AUTH_SECRET: z.string().optional(),
      LISTEN_CONNECT_PUBLIC_KEY: requiredIf(isVercelDeployment),
      SLACK_LINK_RESOLUTION_PUBLIC_KEY: requiredIf(isVercelDeployment),
      SLACK_CUSTOMER_CLIENT_ID: z.string().optional(),
      SLACK_CUSTOMER_CLIENT_SECRET: z.string().optional(),
      SLACK_CUSTOMER_SIGNING_SECRET: z.string().optional(),
      // Local development only: routes the Slack OAuth callback through a tunnel.
      SLACK_CUSTOMER_REDIRECT_URI: z.string().url().optional(),
      // Verifies Slack request signatures for the Listen Research bot.
      SLACK_RESEARCH_SIGNING_SECRET: z.string().optional(),
      AI_TEMP: z.string().optional(),

      // Edge middleware Datadog metrics (middleware may not import sharedBackendEnv).
      DD_API_KEY: z.string().optional(),

      // Render Workflows starters (optional until Infisical is wired).
      RENDER_API_KEY: z.string().optional(),
      RENDER_WORKFLOW_SLUG: z.string().optional(),
      RENDER_USE_LOCAL_DEV: z.string().default("false"),
      RENDER_LOCAL_DEV_URL: z.string().optional(),

      // Read by next.config.ts. CI test builds set "1"; the required typecheck job covers these types.
      SKIP_NEXT_BUILD_TYPECHECK: z.enum(["0", "1"]).optional(),
      // Read by next.config.ts. Only the Spectest image sets "1"; a deployed build must never expose the testing API.
      EXPOSE_NEXT_TESTING_API: z.enum(["0", "1"]).optional(),
    });
  },
});

export const nextAppPublicEnvDefinition = defineEnvSchema({
  // Workers parse clientEnv through backend/instrumentation.ts for Sentry setup.
  envConsumers: ["next-app", "temporal-worker", "jobs-worker"],
  schema: (context) => z.object(getClientEnvBaseSchema(context)),
});

export const nextAppClientEnvDefinition = defineEnvSchema({
  envConsumers: ["next-app"],
  schema: ({ nodeEnv, publicVercelEnv }) =>
    z.object({
      NEXT_PUBLIC_POSTHOG_MARKETING_CAPTURE_KEY: requiredIf(
        nodeEnv === "production"
      ),
      NEXT_PUBLIC_TURNSTILE_SITE_KEY: requiredIf(
        publicVercelEnv === "production" || publicVercelEnv === "preview"
      ),
      NEXT_PUBLIC_API_SIGNATURE_KEY: z.string(),
    }),
});
